// eslint-disable-next-line import/no-unresolved
import type { Content } from '@plone/types';
import type { OccasionProps } from '@fhnw/types/occasion';

const EMPTY_VALUE = '-';
const ANALYTICS_DATE_FORMATTER = new Intl.DateTimeFormat('de-CH', {
  timeZone: 'Europe/Zurich',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

export interface AnalyticsBuildOptions {
  pathname?: string;
  search?: string;
  occasion?: Partial<OccasionProps>;
}

export interface AnalyticsPayload {
  event: 'page_view';
  Seitentyp: string;
  Hochschule: string;
  Anlass: string;
  Stufe: string;
  Produkt: string;
  Schritt: string;
  Anmeldeart: string;
  Typ: string;
  Ort: string;
  Datum: string;
  Sprache: string;
  HasForm: string;
}

interface TermLike {
  token?: string;
  value?: string;
  title?: string;
}

const ANALYTICS_PAGE_TYPES = new Set([
  'LRF',
  'DegreeProgram',
  'Service',
  'InfoEvent',
  'FHNWEvent',
  'ResearchArea',
  'ResearchFocus',
  'University',
  'Contact',
  'TeamContact',
  'Module',
  'FHNWNews',
  'Project',
  'Document',
  'Topic',
  'Specialization',
  'ContinuingEducation',
  'Landingpage',
]);

const PAGE_TYPE_ANLASS: Record<string, string> = {
  DegreeProgram: 'Studium',
  ContinuingEducation: 'Weiterbildung',
  InfoEvent: 'Infoanlass',
  FHNWEvent: 'Veranstaltung',
  Module: 'Modul',
};

const LOCATION_AND_DATE_PAGE_TYPES = new Set([
  'DegreeProgram',
  'ContinuingEducation',
  'InfoEvent',
  'FHNWEvent',
]);

const SIMPLE_TERM_MAP: Record<string, string> = {
  'bachelor of science': 'Bachelor of Science',
  bachelor_science: 'Bachelor of Science',
  bachelor_of_science: 'Bachelor of Science',
  bsc: 'Bachelor of Science',
  'bachelor of arts': 'Bachelor of Arts',
  bachelor_arts: 'Bachelor of Arts',
  bachelor_of_arts: 'Bachelor of Arts',
  ba: 'Bachelor of Arts',
  'master of science': 'Master of Science',
  master_science: 'Master of Science',
  master_of_science: 'Master of Science',
  msc: 'Master of Science',
  'master of arts': 'Master of Arts',
  master_arts: 'Master of Arts',
  master_of_arts: 'Master of Arts',
  ma: 'Master of Arts',
  'summer school': 'Summer School',
  summer_school: 'Summer School',
  lehrdiplom: 'Lehrdiplom',
  teaching_diploma: 'Lehrdiplom',
  cas: 'CAS',
  das: 'DAS',
  mas: 'MAS',
  mba: 'MBA',
  sas: 'SAS',
  modul: 'Modul',
  module: 'Modul',
  kurs: 'Kurs',
  course: 'Kurs',
  fachseminar: 'Fachseminar',
  specialist_seminar: 'Fachseminar',
  fachtagung: 'Fachtagung',
  symposium: 'Fachtagung',
  kurzzeitprogramm: 'Kurzzeitprogramm',
  short_term_program: 'Kurzzeitprogramm',
  short_program: 'Kurzzeitprogramm',
  master: 'Master',
  bachelor: 'Bachelor',
  tagung: 'Tagung',
  conference: 'Tagung',
  seminar: 'Seminar',
  'dual degree': 'Dual Degree',
  dual_degree: 'Dual Degree',
  trinational: 'Trinational',
  andere: 'Andere',
  other: 'Andere',
  news: 'News',
  story: 'Story',
  medienmitteilung: 'Medienmitteilung',
  media_release: 'Medienmitteilung',
  press_release: 'Medienmitteilung',
  'press release': 'Medienmitteilung',
  'e-magazin': 'E-Magazin',
  e_magazine: 'E-Magazin',
  'e-magazine': 'E-Magazin',
  publikation: 'Publikation',
  publication: 'Publikation',
  forschungsprojekt: 'Forschungsprojekt',
  research_project: 'Forschungsprojekt',
  'research project': 'Forschungsprojekt',
  studierendenprojekt: 'Studierendenprojekt',
  student_project: 'Studierendenprojekt',
  'student project': 'Studierendenprojekt',
  basel: 'Basel',
  'brugg-windisch': 'Brugg-Windisch',
  brugg_windisch: 'Brugg-Windisch',
  muttenz: 'Muttenz',
  olten: 'Olten',
  online: 'Online',
  solothurn: 'Solothurn',
};

const EVENT_TYPE_MAP: Record<string, string> = {
  study_info_event: 'Info-Anlass Studium',
  degree_program_info_event: 'Info-Anlass Studium',
  info_event_degree_programmes: 'Info-Anlass Studium',
  'study information event': 'Info-Anlass Studium',
  info_anlass_studium: 'Info-Anlass Studium',
  continuing_education_info_event: 'Info-Anlass Weiterbildung',
  continuingeducation_info_event: 'Info-Anlass Weiterbildung',
  info_event_continuing_education: 'Info-Anlass Weiterbildung',
  'continuing education information event': 'Info-Anlass Weiterbildung',
  info_anlass_weiterbildung: 'Info-Anlass Weiterbildung',
  anderes: 'Anderes',
  other: 'Anderes',
  ausstellung_praesentation: 'Ausstellung/Präsentation',
  ausstellung_prasentation: 'Ausstellung/Präsentation',
  exhibition_presentation: 'Ausstellung/Präsentation',
  'exhibition/presentation': 'Ausstellung/Präsentation',
  konzert: 'Konzert',
  concert: 'Konzert',
  ringvorlesung: 'Ringvorlesung',
  lecture_series: 'Ringvorlesung',
  tagung_kongress: 'Tagung/Kongress',
  conference_congress: 'Tagung/Kongress',
  'conference/congress': 'Tagung/Kongress',
  workshop_seminar: 'Workshop/Seminar',
  'workshop/seminar': 'Workshop/Seminar',
};

const UNIVERSITY_MAP: Record<string, string[]> = {
  APS: ['aps', 'angewandte-psychologie', 'applied-psychology', 'psychologie'],
  HABG: ['habg', 'architektur-bau-geomatik', 'architecture-civil-engineering'],
  HGK: ['hgk', 'gestaltung-kunst', 'design-art'],
  HSI: ['hsi', 'informatik', 'computer-science'],
  HLS: ['hls', 'life-sciences'],
  HSM: ['hsm', 'musik', 'music'],
  PH: ['ph', 'paedagogische-hochschule', 'pädagogische-hochschule'],
  HSA: ['hsa', 'soziale-arbeit', 'social-work'],
  HTU: ['htu', 'technik', 'engineering'],
  HSW: ['hsw', 'wirtschaft', 'business', 'economie'],
};

const normalizeKey = (value?: string | null) =>
  (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[()]/g, '')
    .replace(/\s+/g, '_');

const isPlainObject = (value: unknown): value is Record<string, any> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

const termCandidates = (term: unknown): string[] => {
  if (Array.isArray(term)) {
    return term.flatMap(termCandidates);
  }
  if (typeof term === 'string') {
    return [term];
  }
  if (!isPlainObject(term)) {
    return [];
  }
  return [term.token, term.value, term.title].filter(Boolean) as string[];
};

const normalizeTerm = (
  term: unknown,
  map: Record<string, string> = SIMPLE_TERM_MAP,
) => {
  for (const candidate of termCandidates(term)) {
    const normalized = normalizeKey(candidate);
    const spaceNormalized = normalized.replace(/_/g, ' ');
    if (map[normalized]) {
      return map[normalized];
    }
    if (map[spaceNormalized]) {
      return map[spaceNormalized];
    }
  }

  return termCandidates(term)[0] || EMPTY_VALUE;
};

const normalizeTerms = (
  terms: unknown,
  map: Record<string, string> = SIMPLE_TERM_MAP,
) => {
  if (!Array.isArray(terms)) {
    return normalizeTerm(terms, map);
  }

  const normalizedTerms = terms
    .map((term) => normalizeTerm(term, map))
    .filter((term) => term && term !== EMPTY_VALUE);

  return normalizedTerms.length ? normalizedTerms.join(', ') : EMPTY_VALUE;
};

const hasSendParam = (search?: string) => {
  if (!search) {
    return false;
  }
  return new URLSearchParams(search).get('send') === 'true';
};

export const formatAnalyticsDate = (value?: string | Date | null) => {
  if (!value) {
    return EMPTY_VALUE;
  }

  if (typeof value === 'string') {
    const swissDate = value.match(/^(\d{2})\.(\d{2})\.(\d{4})/);
    if (swissDate) {
      return swissDate[0];
    }
    const dateOnly = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnly) {
      return `${dateOnly[3]}.${dateOnly[2]}.${dateOnly[1]}`;
    }
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return EMPTY_VALUE;
  }

  return ANALYTICS_DATE_FORMATTER.format(date);
};

export const hasAnalyticsFormBlock = (blocks?: Record<string, any>) => {
  const visited = new Set<unknown>();
  const formBlockTypes = new Set([
    'schemaForm',
    'crmformblock',
    'form',
    'newsletter',
  ]);

  const visit = (value: unknown): boolean => {
    if (!value || typeof value !== 'object' || visited.has(value)) {
      return false;
    }
    visited.add(value);

    if (Array.isArray(value)) {
      return value.some(visit);
    }

    const objectValue = value as Record<string, any>;
    if (formBlockTypes.has(objectValue['@type'])) {
      return true;
    }

    return Object.values(objectValue).some(visit);
  };

  return visit(blocks);
};

const getLanguage = (content: Content, pathname?: string) => {
  const language =
    (content?.language as TermLike | undefined)?.token ||
    pathname?.split('/').filter(Boolean)[0] ||
    content?.['@id']?.split('/').filter(Boolean)[0] ||
    'de';

  return ['de', 'en', 'fr'].includes(language.toLowerCase())
    ? language.toUpperCase()
    : 'DE';
};

const LANGUAGE_SEGMENTS = new Set(['de', 'en', 'fr']);

// Universities are always top-level sections, so path candidates only count
// via the section slug right after the language segment (decision Christof,
// #1706): /de/themen/... deliberately stays FHNW unless analytics_university
// overrides it.
const pathCandidateSlug = (candidate: string) => {
  const segments = candidate.split('/').filter(Boolean);
  const languageIndex = segments.findIndex((segment) =>
    LANGUAGE_SEGMENTS.has(segment.toLowerCase()),
  );
  return languageIndex === -1 ? undefined : segments[languageIndex + 1];
};

// Single-word tokens must match a whole path/word segment, otherwise
// e.g. 'psychologie' would claim the slug wirtschaftspsychologie for APS.
const matchesUniversityCandidate = (
  candidate: string,
  universityCandidate: string,
) => {
  if (!universityCandidate.includes('-')) {
    return candidate
      .split(/[^a-z0-9]+/)
      .filter(Boolean)
      .includes(universityCandidate);
  }

  return candidate.includes(universityCandidate);
};

// Maintained analytics_university values (taxonomy universityshort) map onto
// the short codes directly; anything else falls back to structure/URL matching.
const getMaintainedUniversity = (content: Content) => {
  for (const candidate of termCandidates(
    (content as any).analytics_university,
  )) {
    const normalized = normalizeKey(candidate);
    if (normalized === 'fhnw') {
      return 'FHNW';
    }
    for (const [shortCode, universityCandidates] of Object.entries(
      UNIVERSITY_MAP,
    )) {
      if (universityCandidates.includes(normalized)) {
        return shortCode;
      }
    }
  }

  return null;
};

const getUniversity = (content: Content) => {
  const maintainedUniversity = getMaintainedUniversity(content);
  if (maintainedUniversity) {
    return maintainedUniversity;
  }

  const structuredUniversity =
    content?.['@type'] === 'University'
      ? content
      : content?.['@components']?.university;
  const university = structuredUniversity as
    | (Content & {
        id?: string;
        nav_title?: string;
        teaser_title?: string;
      })
    | undefined;

  const candidates = [
    university?.['@id'],
    university?.id,
    university?.nav_title,
    university?.title,
    university?.teaser_title,
    content?.['@id'],
  ]
    .filter(Boolean)
    .map((candidate) =>
      (candidate as string).includes('/')
        ? pathCandidateSlug(candidate as string)
        : (candidate as string),
    )
    .filter(Boolean)
    .map((candidate) => normalizeKey(candidate as string));

  // Candidate order encodes priority: the structural university (navtree
  // expander) must win over the page's own path.
  for (const candidate of candidates) {
    for (const [shortCode, universityCandidates] of Object.entries(
      UNIVERSITY_MAP,
    )) {
      if (
        universityCandidates.some((universityCandidate) =>
          matchesUniversityCandidate(candidate, universityCandidate),
        )
      ) {
        return shortCode;
      }
    }
  }

  return 'FHNW';
};

const getStufe = (content: Content) => {
  if (content?.['@type'] === 'DegreeProgram') {
    return normalizeTerm(content.degree_program_type as TermLike);
  }
  if (content?.['@type'] === 'ContinuingEducation') {
    return normalizeTerm(content.continuing_education_type as TermLike);
  }
  if (
    content?.['@type'] === 'InfoEvent' ||
    content?.['@type'] === 'FHNWEvent'
  ) {
    return normalizeTerm((content as any).analytics_degree as TermLike);
  }

  return EMPTY_VALUE;
};

const getProdukt = (content: Content) => {
  if (
    content?.['@type'] === 'DegreeProgram' ||
    content?.['@type'] === 'ContinuingEducation'
  ) {
    return content.product_name_without_type || content.title || EMPTY_VALUE;
  }

  return content?.title || EMPTY_VALUE;
};

const getTyp = (content: Content) => {
  if (
    content?.['@type'] === 'InfoEvent' ||
    content?.['@type'] === 'FHNWEvent'
  ) {
    return normalizeTerm(content.event_type as any, EVENT_TYPE_MAP);
  }
  if (content?.['@type'] === 'FHNWNews') {
    return normalizeTerm(content.news_category as any);
  }
  if (content?.['@type'] === 'Project') {
    return normalizeTerm(content.project_type as any);
  }

  return EMPTY_VALUE;
};

const getOrt = (content: Content, occasion?: Partial<OccasionProps>) => {
  if (!LOCATION_AND_DATE_PAGE_TYPES.has(content?.['@type'] || '')) {
    return EMPTY_VALUE;
  }

  const occasionPlace = occasion?.place?.trim();
  if (occasionPlace) {
    return occasionPlace;
  }

  return normalizeTerms(content.location);
};

const getDatum = (content: Content, occasion?: Partial<OccasionProps>) => {
  if (!LOCATION_AND_DATE_PAGE_TYPES.has(content?.['@type'] || '')) {
    return EMPTY_VALUE;
  }

  return occasion?.occasion_start
    ? formatAnalyticsDate(occasion.occasion_start)
    : EMPTY_VALUE;
};

export const buildAnalyticsPayload = (
  content?: Content,
  options: AnalyticsBuildOptions = {},
): AnalyticsPayload | null => {
  if (!content?.['@type'] || !ANALYTICS_PAGE_TYPES.has(content['@type'])) {
    return null;
  }

  const hasForm = hasAnalyticsFormBlock(content.blocks);
  const isSent = hasForm && hasSendParam(options.search);

  return {
    event: 'page_view',
    Seitentyp: content['@type'],
    Hochschule: getUniversity(content),
    Anlass: PAGE_TYPE_ANLASS[content['@type']] || 'Kein Anlass',
    Stufe: getStufe(content),
    Produkt: getProdukt(content),
    Schritt: isSent ? 'Dankesseite' : 'Infoseite',
    Anmeldeart: isSent ? 'Webformular' : 'Keine Anmeldung',
    Typ: getTyp(content),
    Ort: getOrt(content, options.occasion),
    Datum: getDatum(content, options.occasion),
    Sprache: getLanguage(content, options.pathname),
    HasForm: hasForm ? 'Ja' : 'Nein',
  };
};

export const pushAnalyticsPayload = (payload: AnalyticsPayload | null) => {
  if (!payload || typeof window === 'undefined') {
    return;
  }

  const analyticsWindow = window as typeof window & {
    dataLayer?: AnalyticsPayload[];
  };
  analyticsWindow.dataLayer = analyticsWindow.dataLayer || [];
  analyticsWindow.dataLayer.push(payload);
};

export const pushAnalyticsPageview = (
  content?: Content,
  options: AnalyticsBuildOptions = {},
) => {
  pushAnalyticsPayload(buildAnalyticsPayload(content, options));
};
